import { useState } from "react";
import { Check, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { PRICING, waQuote } from "../data/site";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Reveal, Kicker } from "../components/Reveal";

const SIZE_KEYS = ["S", "M", "XL"];

export default function PacksPage() {
  const { lang, t } = useLang();
  const prefix = lang === "ca" ? "/ca" : "";
  const [size, setSize] = useState(1);
  const sizeIdx = (k) => SIZE_KEYS.indexOf(k);

  const PriceTable = ({ title, rows }) => (
    <Reveal>
      <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#121212]">
        <div className="border-b border-white/5 bg-[#171717] px-6 py-4">
          <h2 className="font-display text-xl font-bold uppercase tracking-wide text-[#8BFF32]">{title}</h2>
        </div>
        <div className="grid grid-cols-4 gap-2 border-b border-white/5 px-6 py-3 font-mono2 text-[11px] uppercase tracking-widest text-zinc-500">
          <span>{lang === "ca" ? "Servei" : "Servicio"}</span>
          {SIZE_KEYS.map((s) => (
            <span key={s} className={`text-right ${size === sizeIdx(s) ? "text-[#8BFF32]" : ""}`}>{s}</span>
          ))}
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-4 items-center gap-2 border-b border-white/5 px-6 py-4 last:border-0">
            <span className="text-sm font-medium text-white">{r[lang] || r.es}</span>
            {r.prices.map((p, j) => (
              <span
                key={j}
                className={`text-right font-display text-lg font-bold ${
                  j === size ? "text-[#8BFF32]" : "text-zinc-500"
                }`}
              >
                {typeof p === "number" ? `${p} €` : `${p} €`}
              </span>
            ))}
          </div>
        ))}
      </div>
    </Reveal>
  );

  return (
    <div data-testid="packs-page" className="pt-32">
      <Seo
        title={t("seo.packs.title")}
        desc={t("seo.packs.desc")}
        path="/packs"
        jsonLd={breadcrumbLd([
          { name: "EcoGlow Detailing", path: prefix || "/" },
          { name: t("nav.packs"), path: `${prefix}/packs` },
        ])}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal><Kicker>{t("packs.kicker")}</Kicker></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("packs.title")}
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">{t("packs.sub")}</p>
        </Reveal>

        {/* Size selector */}
        <Reveal delay={0.25}>
          <div className="mt-10 flex items-center gap-2" data-testid="size-selector">
            {SIZE_KEYS.map((s, i) => (
              <button
                key={s}
                data-testid={`size-${s.toLowerCase()}-button`}
                onClick={() => setSize(i)}
                className={`rounded-full px-6 py-2.5 font-display text-lg font-bold uppercase transition-all duration-300 ${
                  size === i ? "bg-[#63E51D] text-black shadow-[0_0_20px_rgba(99,229,29,0.3)]" : "border border-white/15 text-zinc-400 hover:border-[#63E51D]/50 hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
            <span className="ml-3 hidden text-xs text-zinc-500 sm:block">{t("packs.sizeNote")}</span>
          </div>
        </Reveal>
      </section>

      {/* Packs */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        <Reveal><Kicker>{t("packs.packsTitle")}</Kicker></Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
          {PRICING.packs.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pricing-pack-${p.id}`}
                className={`card-sheen relative flex h-full flex-col rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  p.recommended ? "lime-glow border-[#63E51D] bg-[#101508]" : "border-white/10 bg-[#121212] hover:border-[#63E51D]/40"
                }`}
              >
                {p.recommended && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#63E51D] px-4 py-1 font-mono2 text-[11px] font-bold uppercase tracking-widest text-black">
                    {t("home.recommended")}
                  </span>
                )}
                <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-white">{t(`packs.packNames.${p.id}`)}</h2>
                <p className="mt-4 font-display text-6xl font-black text-[#8BFF32]">{p.prices[size]} €</p>
                <p className="mt-1 font-mono2 text-xs uppercase tracking-widest text-zinc-500">
                  {SIZE_KEYS.map((s, j) => (
                    <span key={s} className={j === size ? "text-[#8BFF32]" : ""}>{s}: {p.prices[j]} €{j < 2 ? " · " : ""}</span>
                  ))}
                </p>
                <h3 className="mt-6 font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">{t("packs.packContents")}</h3>
                <ul className="mt-3 flex-1 space-y-2.5">
                  {t(`packs.packItems.${p.id}`).map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#63E51D]" /> {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={waQuote(
                    lang === "ca"
                      ? `Hola EcoGlow! M'interessa el pack ${t(`packs.packNames.${p.id}`)} (${SIZE_KEYS[size]}).`
                      : `¡Hola EcoGlow! Me interesa el pack ${t(`packs.packNames.${p.id}`)} (${SIZE_KEYS[size]}).`
                  )}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`pack-quote-${p.id}-button`}
                  className={`mt-8 flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-all duration-300 hover:scale-[1.02] ${
                    p.recommended ? "bg-[#63E51D] text-black hover:shadow-[0_0_25px_rgba(99,229,29,0.4)]" : "border border-[#63E51D]/50 text-[#8BFF32] hover:bg-[#63E51D]/10"
                  }`}
                >
                  <MessageCircle className="h-5 w-5" /> {t("packs.cta")}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Price tables */}
      <section className="bg-[#080808] py-16 lg:py-24">
        <div className="mx-auto max-w-5xl space-y-8 px-4 sm:px-6 lg:px-12">
          <PriceTable title={t("packs.cleaning")} rows={PRICING.cleaning} />
          <PriceTable title={t("packs.correction")} rows={PRICING.correction} />
          <PriceTable title={t("packs.protection")} rows={PRICING.protection} />
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#121212]">
              <div className="border-b border-white/5 bg-[#171717] px-6 py-4">
                <h2 className="font-display text-xl font-bold uppercase tracking-wide text-[#8BFF32]">{t("packs.extras")}</h2>
              </div>
              {PRICING.extras.map((ex, i) => (
                <div key={i} className="flex items-center justify-between border-b border-white/5 px-6 py-4 last:border-0">
                  <span className="text-sm font-medium text-white">{ex[lang] || ex.es}</span>
                  <span className="font-display text-lg font-bold text-[#8BFF32]">{lang === "ca" && ex.priceCa ? ex.priceCa : ex.price}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <p className="text-xs leading-relaxed text-zinc-500">{t("packs.note")}</p>
        </div>
      </section>
    </div>
  );
}
